import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DeliveryServiceClient } from '../../api/ph-web/delivery/delivery.apiServiceClientPb';
import { GetPermissionsByUserIdRequest } from '../../api/ph-web/delivery/delivery.api_pb';
import { ConnectivityService } from '../../sharedServices/connectivity.service';
import { GrpcFactoryService } from '../../sharedServices/grpc-factory.service';
import { UserDataService } from '../../sharedServices/user-data.service';
import { PermissionShared } from './models/permission.interface';
import { PermissionOption } from './models/permissionOption.interface';
import { CountryShared } from '../../models/interface/country.interface';
import { PermissionPaymentsOption } from './keys/permissionPayments.enum';
import { PermissionExpressBasicOption } from './keys/permissionExpressBasic.enum';
import { UserType } from '../../models/interface/userType.enum';
import { UserDataShared } from '../../models/interface/userData.interface';
import { PermissionLoadConfigShared } from '../../models/interface/permissionLoadConfig.interface';
import { PermissionBaseType } from './models/permissionBaseType.enum';
import { allPermissionOptionList } from '../../sharedConstants/menu.constants';
import { PermissionUserOption } from './keys/permissionUser.interface';

@Injectable({
  providedIn: 'root',
})
export class PermissionLoadDataService {
  private deliveryClient = this.grpcFactoryService.create(
    DeliveryServiceClient,
    `${environment.hostname}${environment.hostPort}`,
    'DeliveryServiceClient'
  );
  private getPermissionListCall = this.grpcFactoryService.call(this.deliveryClient, this.deliveryClient.getPermissionsByUserId, {
    onBefore: this.connectivityService.checkServerAvailability.bind(this.connectivityService),
    callName: 'getPermissionsByUserId',
  });

  private permissionData: Promise<PermissionShared[]>;
  private featureFlagData: Promise<{ [key: string]: boolean }>;
  private allFlagList: PermissionOption[] = [];
  private allPermissionList: PermissionOption[] = allPermissionOptionList;

  constructor(
    private userDataService: UserDataService,
    private grpcFactoryService: GrpcFactoryService,
    private connectivityService: ConnectivityService
  ) {
    this.allFlagList = this.allPermissionList.filter(x => x.type === PermissionBaseType.FLAG);
  }

  /**
   * method to get permissions by user id
   *
   * @param {string} filterName
   * @param {string} filterValue
   * @return {PermissionShared[]} response
   */
  async loadPermissionListByUser(config: PermissionLoadConfigShared): Promise<PermissionShared[]> {
    if (this.permissionData && !config?.userData) {
      return this.permissionData;
    }
// eslint-disable-next-line no-async-promise-executor
    this.permissionData = new Promise<PermissionShared[]>(async (resolve, reject) => {
      try {
        resolve(await this.getPermissionList(config));
      } catch (ex) {
        reject(ex);
      }
    });
    return this.permissionData;
  }

  /**
   * load flag list
   *
   * @param {PermissionOption[]} allFlagList
   * @returns
   */
  async loadFlagList(config: PermissionLoadConfigShared): Promise<{ [key: string]: boolean }> {
    if (localStorage.getItem('resetFlags')?.toString() === '1') {
      const tmpResponse = this.featureFlagData;
      this.featureFlagData = undefined;
      localStorage.setItem('resetFlags', '0');
      return tmpResponse;
    }
    if (this.featureFlagData) {
      return this.featureFlagData;
    }
// eslint-disable-next-line no-async-promise-executor
    this.featureFlagData = new Promise<{ [key: string]: boolean }>(async (resolve, reject) => {
      try {
        resolve(await this.getFlagList(config));
      } catch (ex) {
        reject(ex);
      }
    });
    return this.featureFlagData;
  }

  /**
   * reset permission data
   */
  reset(): void {
    this.permissionData = null;
  }

  /**
   * disable special permission, temporal
   * @param {PermissionShared[]} localList
   * @returns {PermissionShared[]} response
   */
  async disableSpecialPermissions(localList: PermissionShared[], userData?: UserDataShared): Promise<PermissionShared[]> {
    let localUserData = userData || (await this.userDataService.getUserDataAsPromise());
    const paymentKey = 'payments';
    const profileKey = 'profile';

    // Code to activate business rules only wallet to admin profile
    // if (localUserData.userType === UserType.COMPANY && localUserData.administrator === false && environment.production) {
    //   localList = localList.filter(x => x.application !== paymentKey);
    // }
    localUserData.roleIdList = localUserData?.roleIdList ? localUserData?.roleIdList :[]
    if (localUserData.administrator === false && !localUserData?.roleIdList.includes('5') && !localUserData?.roleIdList.includes('6')) {
      localList = localList.filter(x => x.application !== profileKey);
    }

    //Remove the next if to active Transfer MX
    if (localUserData.countryId === 3 && environment.production) {
      localList = localList.filter(x => x.application !== paymentKey);
    }
    return localList;
  }

  /**
   * get permissionList
   * @param {PermissionLoadConfigShared} config
   * @returns {PermissionShared[]} response
   */
  async getPermissionList(config: PermissionLoadConfigShared): Promise<PermissionShared[]> {
    const localUserData = config?.userData || (await this.userDataService.getUserDataAsPromise());
    const permissionListRequest = new GetPermissionsByUserIdRequest();
    permissionListRequest.setUserid(parseInt(localUserData.id, 10));
    permissionListRequest.setFiltername(environment.shared.filterForPermission.filterName);
    permissionListRequest.setFiltervalue(environment.shared.filterForPermission.filterValue);

    let responseList: PermissionShared[];
    if (localUserData.username) {
      const response = await this.getPermissionListCall(permissionListRequest);
      const permissionList: PermissionShared[] = response?.getDataList()?.map(permission => {
        return {
          application: permission.getAplication(),
          description: permission.getDescription(),
          module: permission.getModule(),
          name: permission.getName(),
          type: permission.getPermission(),
        };
      });
      responseList = permissionList.filter(item => {
        const data = Object.entries(item).filter(([_, value]) => value.length > 0);
        return data.length > 0;
      });
      responseList = await this.disableSpecialPermissions(responseList, localUserData);
    }
    return responseList;
  }

  /**
   * get flag list
   * @param {} config
   * @returns
   */
  async getFlagList(config?: PermissionLoadConfigShared): Promise<{ [key: string]: boolean }> {
    const responseList: { [key: string]: boolean } = {};
    const country: CountryShared =
      config?.country ||
      environment.countries.find(x => x.geoName === localStorage.getItem('location')?.toString()) ||
      environment.countries[0];
    const flagListItem = this.allFlagList.filter(x => !x.country || x.country === country.geoName);
    for (const flag of flagListItem) {
      if (
        [PermissionExpressBasicOption.EXPRESS_BASIC_FLAG_PREPAID_MX.toString(), PermissionUserOption.USER_SOCKET_FRONT.toString()].includes(
          flag.permissions[0]?.toString()
        )
      ) {
        responseList[flag.permissions[0]] = false;
      } else {
        responseList[flag.permissions[0]] = true;
      }
    }
    return responseList;
  }
}
